import React, { useState, useEffect, useRef, Fragment } from 'react';
import axios from 'axios';
import classes from './TrackingSearch.module.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faShoppingCart, faClipboardCheck, faCheckDouble, faClock, faPrint, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';

const API_BASE = 'https://new.api.orders.e-interior.gr/api';

// ─── helpers ────────────────────────────────────────────────────────────────

function parseOptions(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

function fmtEuro(val) {
  const n = parseFloat(val);
  if (!Number.isFinite(n) || n === 0) return null;
  return n.toFixed(2).replace('.', ',') + ' €';
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
}

function toNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function VatNote() {
  return <span style={{ fontSize: '0.74em', color: '#aaa', marginLeft: 3 }}>με ΦΠΑ</span>;
}

// ─── product options summary (mirrors PrintableOrder logic) ─────────────────

function productDimensionSummary(type = '', opts = {}) {
  const t = type;
  if (t.includes('Ύφασμα') && !t.includes('Τραπεζομάντηλο') && !t.includes('Άλλη') && !t.includes('χωρίς')) {
    const m2 = (toNum(opts.calculatedWidth) * toNum(opts.curtainHeight)).toFixed(2);
    const rod = toNum(opts.rodWidth).toFixed(2);
    const sewing = opts.sewingSelection === 'Άλλη ραφή' ? opts.rafiName : String(opts.sewingSelection || '').split('(')[0].trim();
    return `${m2}m² · κουρτινόξυλο ${rod}m · ${sewing}`;
  }
  if (t.includes('Τραπεζομάντηλο')) {
    return `${(toNum(opts.widthTrapezi) * toNum(opts.lengthTrapezi)).toFixed(2)}m²`;
  }
  if (t.includes('Άλλη κατασκευή') || t.includes('χωρίς ραφή')) {
    return `${toNum(opts.fabricMeters).toFixed(2)}m`;
  }
  if (t.includes('Κουρτινόξυλο')) {
    return `${toNum(opts.rodWidth).toFixed(2)}m πλάτος`;
  }
  if (t.includes('roller') || t.includes('Περσίδες')) {
    return `${(toNum(opts.rollerWidth) * toNum(opts.rollerHeight)).toFixed(2)}m²`;
  }
  if (t.includes('Ρόμαν') && !t.includes('Ψευτο')) {
    return `${(toNum(opts.romanWidth) * toNum(opts.romanHeight)).toFixed(2)}m² ${opts.liningSelection || ''}`.trim();
  }
  if (t.includes('Ψευτορόμαν')) {
    return `${(toNum(opts.rodWidth) * toNum(opts.romanWidth)).toFixed(2)}m²`;
  }
  if (t.includes('Μοκέτα') || t.includes('Πλαστικό') || t.includes('Γκαζόν') || t.includes('Διάδρομος')) {
    return `${(toNum(opts.width) * toNum(opts.length)).toFixed(2)}m²`;
  }
  if (t === 'Έτοιμο χαλί' && opts.dimensions) return opts.dimensions;
  if (t === 'Σετ Κρεβατοκάμαρας') {
    return [opts.dimensionsOne, opts.dimensionsTwo, opts.dimensionsThree].filter(Boolean).join(' · ');
  }
  const qty = toNum(opts.quantity);
  if (qty > 1) return `×${qty}`;
  return '';
}

// ─── status badge ────────────────────────────────────────────────────────────

const STATUS_LABEL = {
  submitted: 'Καταχωρήθηκε', ordered: 'Παραγγέλθηκε', rafeio: 'Στο Ραφείο',
  modify: 'Επεξεργασία', merikos: 'Μερική Παράδοση', ready: 'Έτοιμο',
  sent: 'Παραδόθηκε', send: 'Παραδόθηκε', repair: 'Επιδιόρθωση',
  communicated: 'Επικοινωνήθηκε', proxeiro: 'Προσφορά',
};
const STATUS_COLOR = {
  submitted: { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  ordered:   { bg: '#e0e7ff', text: '#3730a3', dot: '#6366f1' },
  rafeio:    { bg: '#fce7f3', text: '#9d174d', dot: '#ec4899' },
  modify:    { bg: '#fae8ff', text: '#86198f', dot: '#a855f7' },
  merikos:   { bg: '#fed7aa', text: '#c2410c', dot: '#f97316' },
  ready:     { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
  sent:      { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  send:      { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  repair:    { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
};

function StatusBadge({ status }) {
  const key = String(status || 'submitted').toLowerCase();
  const label = STATUS_LABEL[key] || key;
  const col = STATUS_COLOR[key] || { bg: '#f1f5f9', text: '#475569', dot: '#94a3b8' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 9999,
      background: col.bg, color: col.text,
      fontSize: '0.73rem', fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: col.dot, display: 'inline-block' }} />
      {label}
    </span>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const card = {
  background: '#fff',
  borderRadius: 18,
  padding: '20px 18px',
  boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
  fontFamily: 'Outfit, sans-serif',
};

const sectionLabel = {
  fontSize: '0.72rem', fontWeight: 700, color: '#bbb',
  textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8,
};

const divider = { borderTop: '1px solid #f0ece5', margin: '12px 0' };

const rowBetween = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 };

// ─── Card 1: Order info + payment + contact ──────────────────────────────────

function CardInfo({ orderData }) {
  const payment = parseOptions(orderData.payment);
  const synolo = toNum(payment.synolo || payment.total);
  const initialPrice = toNum(payment.initialPrice || payment.initial_price);
  // `payment.detailed` is sometimes a real array (payments added later, from the
  // in-progress order screen) and sometimes a JSON-encoded *string* nested inside the
  // payment object (payments recorded at order-creation time, see StepExtras.jsx —
  // it does `detailed: JSON.stringify(detailed)`). Without handling the string case,
  // an advance payment taken at order creation would silently disappear here and the
  // customer would see "προκαταβολή απαιτείται" even though they already paid.
  const detailedRaw = payment.detailed;
  let detailed = [];
  if (typeof detailedRaw === 'string') {
    try { detailed = JSON.parse(detailedRaw); } catch { detailed = []; }
  } else if (Array.isArray(detailedRaw)) {
    detailed = detailedRaw;
  }
  if (!Array.isArray(detailed)) detailed = [];
  const totalPaid = detailed.reduce((s, e) => s + toNum(e.amount), 0);
  const balance = synolo - totalPaid;
  const hasPayments = totalPaid > 0.01;
  const isPaidOff = hasPayments && synolo > 0 && balance <= 0.01;

  return (
    <div style={card}>
      {/* Company + order header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1a1a2e', letterSpacing: '-0.02em' }}>
            Μώρος Design House
          </div>
          <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: 2 }}>
            ΧΑΛΙΑ · ΕΙΔΗ ΣΚΙΑΣΗΣ · ΚΟΥΡΤΙΝΟΞΥΛΑ · ΤΑΠΕΤΣΑΡΙΕΣ
          </div>
        </div>
        <button
          onClick={() => window.print()}
          style={{
            background: 'linear-gradient(135deg, #F7941D, #e0850d)',
            border: 0, borderRadius: 10, color: '#fff',
            padding: '6px 12px', fontSize: '0.78rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
            fontFamily: 'Outfit, sans-serif', fontWeight: 600, flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faPrint} style={{ fontSize: '0.75rem' }} />
          Εκτύπωση
        </button>
      </div>

      {/* Order number + dates */}
      <div style={{ background: '#fdf3e7', borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
        <div style={{ fontSize: '0.75rem', color: '#aaa', marginBottom: 2 }}>Αριθμός Παραγγελίας</div>
        <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#F7941D', letterSpacing: '0.02em' }}>
          {orderData.tracking_number}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
          {orderData.created_at && (
            <div style={{ fontSize: '0.78rem', color: '#666' }}>
              <span style={{ color: '#aaa' }}>Ημ/νία: </span>{fmtDate(orderData.created_at)}
            </div>
          )}
          {orderData.deliver_date && (
            <div style={{ fontSize: '0.78rem', color: '#666' }}>
              <span style={{ color: '#aaa' }}>Εκτ. Παράδοση: </span>
              <strong style={{ color: '#F7941D' }}>{fmtDate(orderData.deliver_date)}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Client */}
      {orderData.client_name && (
        <>
          <div style={sectionLabel}>Πελάτης</div>
          <div style={{ fontSize: '0.88rem', color: '#1a1a2e', fontWeight: 600, marginBottom: 2 }}>
            {orderData.client_name}
          </div>
          {orderData.client_phone && (
            <div style={{ fontSize: '0.82rem', color: '#666', marginBottom: 12 }}>
              📞 {orderData.client_phone}
            </div>
          )}
        </>
      )}

      <div style={divider} />

      {/* Payment summary */}
      <div style={sectionLabel}>Σύνοψη Πληρωμής</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: '0.85rem' }}>
        {initialPrice > 0 && initialPrice !== synolo && (
          <div style={rowBetween}>
            <span style={{ color: '#888' }}>Αρχική Τιμή</span>
            <span style={{ color: '#888' }}>{fmtEuro(initialPrice)}<VatNote /></span>
          </div>
        )}
        {synolo > 0 && (
          <div style={{ ...rowBetween, fontWeight: 700, fontSize: '1rem' }}>
            <span style={{ color: '#1a1a2e' }}>Σύνολο</span>
            <span style={{ color: '#F7941D' }}>{fmtEuro(synolo)}<VatNote /></span>
          </div>
        )}
        {hasPayments && (
          <div style={{ ...rowBetween, color: '#16a34a', fontWeight: 600 }}>
            <span>Έχετε καταβάλει</span>
            <span>{fmtEuro(totalPaid)}<VatNote /></span>
          </div>
        )}
        {hasPayments && balance > 0.01 && (
          <div style={{
            ...rowBetween, fontWeight: 700, color: '#dc2626',
            paddingTop: 6, borderTop: '1px dashed #fca5a5', marginTop: 2,
          }}>
            <span>Υπόλοιπο</span>
            <span>{fmtEuro(balance)}<VatNote /></span>
          </div>
        )}
        {isPaidOff && (
          <div style={{
            ...rowBetween, fontWeight: 700, color: '#16a34a',
            paddingTop: 6, borderTop: '1px dashed #86efac', marginTop: 2,
          }}>
            <span>✅ Εξοφλημένο</span>
          </div>
        )}
        {!hasPayments && synolo > 0 && (
          <div style={{ fontSize: '0.78rem', color: '#dc2626', fontStyle: 'italic', marginTop: 2 }}>
            ⚠️ Απαιτείται προκαταβολή για την έναρξη της παραγγελίας.
          </div>
        )}
      </div>

      {/* Payment breakdown */}
      {hasPayments && detailed.length > 0 && (
        <>
          <div style={{ ...divider, marginTop: 10 }} />
          <div style={{ ...sectionLabel, marginBottom: 6 }}>Ανάλυση Πληρωμών</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {detailed.map((e, i) => {
              const amt = toNum(e.amount);
              if (!amt) return null;
              const dateStr = e.date ? fmtDate(e.date) : '';
              const howMap = {
                cash: 'Μετρητά', card: 'Κάρτα', transfer: 'Τραπεζική', iris: 'IRIS',
                viva: 'Viva', check: 'Επιταγή', ekptosi_stroggylop: 'Έκπτωση στρογγυλοποίησης',
              };
              const how = howMap[e.how] || e.how || '';
              return (
                <div key={i} style={{ fontSize: '0.78rem', color: '#555', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{dateStr && `${dateStr} · `}{how && `με ${how}`}</span>
                  <span style={{ fontWeight: 600 }}>{fmtEuro(amt)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={divider} />

      {/* Bank / payment methods */}
      <div style={sectionLabel}>Τρόποι Πληρωμής</div>
      <div style={{ fontSize: '0.74rem', color: '#666', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div>💳 IRIS στο ΑΦΜ <strong>135 592 599</strong></div>
        <div>🏦 Eurobank <span style={{ color: '#aaa' }}>GR45 0260 2610 0002 6020 1342 081</span></div>
        <div>🏦 Εθνική <span style={{ color: '#aaa' }}>GR70 0110 3140 0000 3140 1010 280</span></div>
        <div>📱 Viva Quickpay <span style={{ color: '#aaa' }}>pay.vivawallet.com/e-interior</span></div>
        <div style={{ marginTop: 2, color: '#aaa' }}>Να αναγράφεται ο αρ. παραγγελίας στην αιτιολογία</div>
      </div>

      <div style={divider} />

      {/* Contact */}
      <div style={sectionLabel}>Επικοινωνία</div>
      <div style={{ fontSize: '0.78rem', color: '#666', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div>📍 Φειδώνος 6, Άργος</div>
        <div>📞 27510 29561</div>
        <div>✉️ morosdesignhouse@gmail.com</div>
      </div>
    </div>
  );
}

// ─── Card 2: Products + extras ───────────────────────────────────────────────

function CardProducts({ orderData, style = {} }) {
  const extras = parseOptions(orderData.extras);
  const paradosi   = toNum(extras.paradosi);
  const metrisi    = toNum(extras.metrisi);
  const topothetisi = Math.max(toNum(extras.topothetisi), toNum(extras.installation), toNum(extras.install));
  const rafis      = toNum(extras.rafis);
  const sideroma   = toNum(extras.sideroma);
  const tunnel     = extras.tunnel_surcharge === true || extras.tunnel_surcharge === 1 || extras.tunnelSurcharge === true;
  const hasExtras  = paradosi > 0 || metrisi > 0 || topothetisi > 0 || rafis > 0 || sideroma > 0 || tunnel;

  const products = (orderData.products || []).filter(p => String(p.status || '').toLowerCase() !== 'deleted');

  return (
    <div style={{ ...card, height: '100%', boxSizing: 'border-box' }}>
      {/* Products */}
      <div style={sectionLabel}>Προϊόντα ({products.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {products.map((p, i) => {
          const opts = parseOptions(p.product_options);
          const totalPrice   = toNum(opts.totalPriceDiscount || opts.totalPrice);
          const origPrice    = toNum(opts.totalPrice);
          const hasDiscount  = opts.totalPriceDiscount && toNum(opts.totalPriceDiscount) < origPrice && origPrice > 0;
          const installPrice = toNum(opts.installPrice);
          const hasInstall   = (opts.installation === true || opts.installation === 1 || opts.install === 'nai' || opts.install === 'ναι') && installPrice > 0;
          const qty          = toNum(opts.quantity) > 1 ? toNum(opts.quantity) : null;
          const dims         = productDimensionSummary(p.product_type, opts);

          return (
            <div key={i} style={{
              background: '#fafaf9', borderRadius: 12,
              padding: '10px 12px', border: '1px solid #f0ece5',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a2e' }}>
                    {p.product_type || '—'}
                  </div>
                  {p.code && (
                    <div style={{ fontSize: '0.73rem', color: '#aaa', marginTop: 1 }}>{p.code}</div>
                  )}
                  {dims && (
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: 2 }}>{dims}</div>
                  )}
                  {qty && (
                    <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 1 }}>×{qty} τεμ.</div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  {totalPrice > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      {hasDiscount && (
                        <div style={{ fontSize: '0.73rem', color: '#aaa', textDecoration: 'line-through' }}>
                          {fmtEuro(origPrice)}
                        </div>
                      )}
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e' }}>
                        {fmtEuro(totalPrice)}<VatNote />
                      </div>
                    </div>
                  )}
                  {hasInstall && (
                    <div style={{ fontSize: '0.73rem', color: '#666' }}>
                      + Τοποθέτηση {fmtEuro(installPrice)}<VatNote />
                    </div>
                  )}
                  <StatusBadge status={p.status} />
                </div>
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <p style={{ color: '#ccc', fontSize: '0.85rem', textAlign: 'center', padding: '12px 0' }}>
            Δεν υπάρχουν προϊόντα
          </p>
        )}
      </div>

      {/* Extras */}
      {hasExtras && (
        <>
          <div style={divider} />
          <div style={sectionLabel}>Επιπλέον Υπηρεσίες</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
            {paradosi > 0 && (
              <div style={rowBetween}><span style={{ color: '#555' }}>Κατ'οίκον Παράδοση</span><span style={{ fontWeight: 600 }}>{fmtEuro(paradosi)}<VatNote /></span></div>
            )}
            {metrisi > 0 && (
              <div style={rowBetween}><span style={{ color: '#555' }}>Κατ'οίκον Μέτρηση</span><span style={{ fontWeight: 600 }}>{fmtEuro(metrisi)}<VatNote /></span></div>
            )}
            {topothetisi > 0 && (
              <div style={rowBetween}><span style={{ color: '#555' }}>Τοποθέτηση</span><span style={{ fontWeight: 600 }}>{fmtEuro(topothetisi)}<VatNote /></span></div>
            )}
            {rafis > 0 && (
              <div style={rowBetween}><span style={{ color: '#555' }}>Ράψιμο</span><span style={{ fontWeight: 600 }}>{fmtEuro(rafis)}<VatNote /></span></div>
            )}
            {sideroma > 0 && (
              <div style={rowBetween}><span style={{ color: '#555' }}>Σιδέρωμα</span><span style={{ fontWeight: 600 }}>{fmtEuro(sideroma)}<VatNote /></span></div>
            )}
            {tunnel && (
              <div style={rowBetween}><span style={{ color: '#555' }}>Συνπτυγμένη πιέτα (Τούνελ)</span><span style={{ fontWeight: 600 }}>20,00 €<VatNote /></span></div>
            )}
          </div>
        </>
      )}

      {/* Disclaimer */}
      <div style={{ ...divider, marginTop: 14 }} />
      <p style={{ fontSize: '0.68rem', color: '#bbb', lineHeight: 1.5, margin: 0 }}>
        Το υπόλοιπο εξοφλείται με την παράδοση/παραλαβή των προϊόντων. Σε περίπτωση τοποθέτησης κατ'οίκον, η εξόφληση γίνεται πριν την τοποθέτηση. Τα εμπορεύματα αποτελούν ιδιοκτησία του καταστήματος μέχρι την εξόφλησή τους. Αλλαγές σε μεταποιημένα ή κατασκευασμένα στα μέτρα σας προϊόντα δε γίνονται. Το παρόν δεν αποτελεί φορολογική απόδειξη.
      </p>
    </div>
  );
}

// ─── Main TrackingSearch ──────────────────────────────────────────────────────

function TrackingSearch() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const [query, setQuery]           = useState('');
  const [orderData, setOrderData]   = useState(null);
  const [trackingRows, setTrackingRows] = useState([]);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingTrack, setLoadingTrack] = useState(false);
  const [orderFetchAttempted, setOrderFetchAttempted] = useState(false);
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (autoSubmittedRef.current) return;
    const params    = new URLSearchParams(location.search);
    const orderParam = params.get('order');
    const trackParam = params.get('track');
    const q = orderParam || trackParam;
    if (q) {
      autoSubmittedRef.current = true;
      setQuery(q);
      if (orderParam) {
        fetchOrderView(orderParam);
        fetchTrackingStatus(orderParam);
      } else {
        fetchTrackingStatus(trackParam);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function fetchOrderView(q) {
    const tn = String(q || '').trim();
    if (!tn) return;
    setLoadingOrder(true);
    setOrderData(null);
    setOrderFetchAttempted(true);
    axios.get(`${API_BASE}/orderPublicView/${encodeURIComponent(tn)}`)
      .then(r => setOrderData(r.data && r.data.tracking_number ? r.data : null))
      .catch(() => setOrderData(null))
      .finally(() => setLoadingOrder(false));
  }

  function fetchTrackingStatus(q) {
    const tn = String(q || '').trim();
    if (!tn) return;
    setLoadingTrack(true);
    setTrackingRows([]);
    axios.post(`${API_BASE}/trackOrder`, { orderNumber: tn })
      .then(r => setTrackingRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTrackingRows([]))
      .finally(() => setLoadingTrack(false));
  }

  function handleFormSubmit(e) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setOrderData(null);
    setOrderFetchAttempted(false);
    fetchTrackingStatus(q);
    const params = new URLSearchParams(location.search);
    params.set('track', q);
    params.delete('order');
    navigate(`?${params.toString()}`);
  }

  const isLoading = loadingOrder || loadingTrack;

  return (
    <Fragment>
      {/* Two-card order layout */}
      {(loadingOrder) && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#F7941D', fontSize: '1.3rem' }}>
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}

      {!loadingOrder && orderData && (
        <div style={{
          display: 'flex', gap: 16, alignItems: 'stretch', flexWrap: 'wrap',
          marginBottom: 24,
        }}>
          <div style={{ flex: '1 1 290px', minWidth: 0 }}>
            <CardInfo orderData={orderData} />
          </div>
          {/* Right column: same height as left, inner scroll */}
          <div style={{ flex: '1 1 290px', minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <CardProducts orderData={orderData} />
            </div>
          </div>
        </div>
      )}

      {!loadingOrder && orderFetchAttempted && !orderData && (
        <p style={{ textAlign: 'center', color: '#ccc', fontSize: '0.9rem', marginBottom: 16 }}>
          Δεν βρέθηκε παραγγελία με αυτόν τον αριθμό.
        </p>
      )}

      {/* Search form — always visible */}
      <form className={classes.trackForm} onSubmit={handleFormSubmit}>
        <label htmlFor="searchInput">Παρακολουθείστε την παραγγελία σας</label><br />
        <div className={classes.trackingNumber}>
          <input
            id="searchInput"
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Αριθμός παραγγελίας (π.χ. MOROS...GR)"
          />
          <button className={classes.searchButton} type="submit" disabled={isLoading}>
            {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
          </button>
        </div>
      </form>

      {/* Tracking status timeline — always below */}
      {loadingTrack && (
        <div style={{ textAlign: 'center', padding: '16px 0', color: '#F7941D' }}>
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      {!loadingTrack && trackingRows.length > 0 && (
        <div className={classes.statusTable}>
          {trackingRows.map((item, index) => (
            <div
              key={index}
              className={`${item.active === '0' || item.active === 0 ? classes.inactive : ''} ${classes.statusRow}`}
            >
              <div className="iconContainer">
                <span style={{ backgroundColor: item.color }} className={classes.statusIcon}>
                  {item.icon === 'faShoppingCart' && <Fragment><FontAwesomeIcon icon={faShoppingCart} /><span className={classes.iconLine} /></Fragment>}
                  {item.icon === 'faClipboardCheck' && <Fragment><FontAwesomeIcon icon={faClipboardCheck} /><span className={classes.iconLine} /></Fragment>}
                  {item.icon === 'faClock' && <Fragment><FontAwesomeIcon icon={faClock} /><span className={classes.iconLine} /></Fragment>}
                  {item.icon === 'faCheckDouble' && <FontAwesomeIcon icon={faCheckDouble} />}
                </span>
              </div>
              <div className={classes.titlesContainer}>
                <h3 className={classes.statusTitle}>{item.title}</h3>
                <p className={classes.statusMessage}>{item.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Fragment>
  );
}

export default TrackingSearch;
